/**
 * O bloco "Alçada da margem" (`TP-064`, `D8`): a pessoa digita a margem da Análise Financeira do IW, vê o que a régua
 * sugere (com a fonte e a versão) e diz quem aprovou. A tela mostra a régua, não decide: quem aprovou diferente do
 * sugerido, ou com a margem no buraco da régua, precisa dizer por quê. O envio à operadora exige este registro.
 */

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ErroDaApi } from '../../../shared/api/erros';
import { formatarDataHora } from '../../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { Identificador } from '../../../shared/ui/Identificador';
import { alcadaDoCaso, chavesDeAlcada, registrarAlcada, sugerirAlcada } from './api';
import {
  NIVEIS_DE_ALCADA,
  ROTULOS_DE_ALCADA,
  formatarPercentual,
  lerMargem,
  rotuloDaFaixa,
  type NivelDeAlcada,
  type RegistroDeAlcada,
  type SugestaoDeAlcada,
} from './tipos';

/** Espera a pessoa parar de digitar antes de pedir a sugestão. */
const ESPERA_DA_DIGITACAO_MS = 300;

function useValorAssentado<T>(valor: T, atrasoMs: number): T {
  const [assentado, setAssentado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => {
      setAssentado(valor);
    }, atrasoMs);
    return () => {
      clearTimeout(t);
    };
  }, [valor, atrasoMs]);
  return assentado;
}

function TextoDaSugestao({ sugestao }: { sugestao: SugestaoDeAlcada }): React.JSX.Element {
  const fonte = sugestao.regua ? ` · ${sugestao.regua.fonte}, versão ${sugestao.regua.versao}` : '';
  if (sugestao.situacao === 'faixa' && sugestao.sugerida !== null) {
    return (
      <p role="status" className="m-0 text-[0.8125rem]">
        Sugerida: <strong>{ROTULOS_DE_ALCADA[sugestao.sugerida]}</strong>
        {sugestao.faixa !== null ? ` (${rotuloDaFaixa(sugestao.faixa)})` : ''}
        <span className="text-[var(--tinta-2)]">{fonte}</span>
      </p>
    );
  }
  if (sugestao.situacao === 'sem_dono') {
    // O buraco da régua é o erro com nome que o card quer levar à Gerência: com o correlation_id do log `alcada.sem_faixa`.
    return (
      <div role="status" className="text-[0.8125rem] text-[var(--roxo)]">
        <p className="m-0">
          {sugestao.mensagem ?? 'A margem não cai em nenhuma faixa da régua. Registre quem aprovou e diga por quê.'}
        </p>
        {sugestao.correlationId !== null ? (
          <p className="m-0 text-[0.75rem] text-[var(--tinta-2)]">
            correlation_id:{' '}
            <Identificador
              valor={sugestao.correlationId}
              titulo="Este é o identificador que a operação usa para achar a execução no log. Ele é carregado, nunca regenerado."
            />
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <p role="status" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
      Nenhuma régua de alçada vigente. Registre quem aprovou e diga por quê.
    </p>
  );
}

function LinhaDoHistorico({ registro }: { registro: RegistroDeAlcada }): React.JSX.Element {
  const sugerida =
    registro.sugerida === null
      ? 'sem alçada sugerida'
      : `sugerida ${ROTULOS_DE_ALCADA[registro.sugerida]}${registro.faixa !== null ? ` (${rotuloDaFaixa(registro.faixa)})` : ''}`;
  const regua = registro.regua ? `, ${registro.regua.fonte} v${registro.regua.versao}` : '';
  return (
    <li className="text-[0.8125rem]">
      <strong>{ROTULOS_DE_ALCADA[registro.aprovadora]}</strong> aprovou a margem de {formatarPercentual(registro.margem)}
      <span className="text-[var(--tinta-2)]">
        {' '}
        · {sugerida}
        {regua} · {formatarDataHora(registro.registradoEm)}
        {registro.registradoPorNome !== null ? ` · por ${registro.registradoPorNome}` : ''}
      </span>
      {registro.motivo !== null ? <div className="text-[var(--tinta-2)]">Motivo: {registro.motivo}</div> : null}
    </li>
  );
}

function FormularioDaAlcada({ casoId, aoRegistrar }: { casoId: string; aoRegistrar?: () => void }): React.JSX.Element {
  const cliente = useQueryClient();
  const [textoDaMargem, setTextoDaMargem] = useState('');
  // `null` = a pessoa ainda não escolheu: vale a sugerida. Derivado, e não copiado num efeito.
  const [escolhida, setEscolhida] = useState<NivelDeAlcada | null>(null);
  const [motivo, setMotivo] = useState('');

  const margem = lerMargem(textoDaMargem);
  const margemAssentada = useValorAssentado(margem, ESPERA_DA_DIGITACAO_MS);

  const sugestao = useQuery({
    queryKey: [...chavesDeAlcada.doCaso(casoId), 'sugestao', margemAssentada],
    queryFn: ({ signal }) => sugerirAlcada(casoId, margemAssentada ?? 0, signal),
    enabled: margemAssentada !== null,
    staleTime: 60_000,
  });

  // A sugestão que vale é a da margem que está no campo: enquanto a digitação não assenta, nenhuma.
  const sugestaoAtual = margemAssentada === margem && sugestao.isSuccess ? sugestao.data : null;

  // Quem aprovou começa pela sugerida; a pessoa troca se a aprovação foi de outra alçada.
  const aprovadora: NivelDeAlcada | '' = escolhida ?? sugestaoAtual?.sugerida ?? '';

  const exigeMotivo = sugestaoAtual !== null && aprovadora !== '' && sugestaoAtual.sugerida !== aprovadora;
  const faltaMotivo = exigeMotivo && motivo.trim() === '';

  const registrar = useMutation({
    mutationFn: () =>
      registrarAlcada(casoId, {
        margem: margem ?? 0,
        aprovadora: aprovadora as NivelDeAlcada,
        motivo: motivo.trim() === '' ? null : motivo.trim(),
      }),
    onSuccess: (atualizada) => {
      cliente.setQueryData(chavesDeAlcada.doCaso(casoId), atualizada);
      setTextoDaMargem('');
      setEscolhida(null);
      setMotivo('');
      aoRegistrar?.();
    },
  });

  const podeEnviar = margem !== null && sugestaoAtual !== null && aprovadora !== '' && !faltaMotivo && !registrar.isPending;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <TextField
          size="small"
          label="Margem (%)"
          value={textoDaMargem}
          onChange={(e) => {
            setTextoDaMargem(e.target.value);
          }}
          error={textoDaMargem.trim() !== '' && margem === null}
          helperText={textoDaMargem.trim() !== '' && margem === null ? 'Percentual com até duas casas, como 8,5' : 'Da Análise Financeira do IW'}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ flex: '0 1 10rem' }}
        />
        <TextField
          select
          size="small"
          label="Quem aprovou"
          value={aprovadora}
          onChange={(e) => {
            setEscolhida(e.target.value as NivelDeAlcada);
          }}
          disabled={sugestaoAtual === null}
          sx={{ flex: '0 1 12rem' }}
        >
          {NIVEIS_DE_ALCADA.map((nivel) => (
            <MenuItem key={nivel} value={nivel}>
              {ROTULOS_DE_ALCADA[nivel]}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {sugestao.isError && margemAssentada === margem ? (
        <p role="alert" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
          {sugestao.error instanceof ErroDaApi ? sugestao.error.message : 'Não foi possível consultar a régua.'}
        </p>
      ) : null}
      {sugestaoAtual !== null ? <TextoDaSugestao sugestao={sugestaoAtual} /> : null}

      {exigeMotivo ? (
        <TextField
          size="small"
          label="Motivo (obrigatório)"
          value={motivo}
          onChange={(e) => {
            setMotivo(e.target.value);
          }}
          helperText="Quem aprovou não é a alçada sugerida, ou a margem não tem faixa na régua."
          fullWidth
        />
      ) : null}

      <Button
        variant="outlined"
        size="small"
        disabled={!podeEnviar}
        onClick={() => {
          registrar.mutate();
        }}
      >
        Registrar aprovação
      </Button>

      {registrar.isError ? (
        <p role="alert" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
          {registrar.error instanceof ErroDaApi ? registrar.error.message : 'Não foi possível registrar. Tente novamente.'}
        </p>
      ) : null}
    </div>
  );
}

/** `aoRegistrar` avisa quem está fora do bloco (o cabeçalho do Caso) que o registro exigido para entregar agora existe. */
export function AlcadaDaMargem({ casoId, aoRegistrar }: { casoId: string; aoRegistrar?: () => void }): React.JSX.Element {
  const consulta = useQuery(alcadaDoCaso(casoId));

  if (consulta.isPending) return <EstadoDeCarregamento linhas={2} />;
  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar a alçada da margem"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const { podeRegistrar, historico } = consulta.data;
  return (
    <div className="space-y-4">
      {podeRegistrar ? (
        <FormularioDaAlcada casoId={casoId} aoRegistrar={aoRegistrar} />
      ) : historico.length === 0 ? (
        <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">A alçada é registrada com o orçamento emitido.</p>
      ) : null}

      {historico.length > 0 ? (
        <ul aria-label="Aprovações registradas" className="m-0 list-none space-y-2 pl-0">
          {historico.map((r) => (
            <LinhaDoHistorico key={r.id} registro={r} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
